import type { BookingService, BookingState } from './types'

export function getBookingServicesCatalog(
  state: BookingState
): BookingService[] {
  return state.quest?.additional_services || []
}

/**
 * Нужен ли отдельный шаг с доп.услугами. Без услуг шаг 2 не показывается:
 * промокод и сертификат переезжают на шаг 1, а «Далее» становится «Забронировать».
 * @param {Pick<BookingState, 'quest'>} state - состояние с текущим квестом
 * @return {boolean} true, если у квеста есть хотя бы одна доп.услуга
 */
export function hasServicesStep(state: Pick<BookingState, 'quest'>): boolean {
  return (state.quest?.additional_services || []).length > 0
}

/**
 * Сумма для варианта "Оплатить всю сумму" (paymentTiming === 'full'):
 * авторитетная Order#full_online_payment_price с сервера, а не
 * state.prepayAmount (тот отражает частичную предоплату, а не полную сумму).
 * Падает обратно на orderTotal, если сервер ещё не прислал это поле,
 * и никогда не превышает саму итоговую сумму заказа.
 *
 * @param {Pick<BookingState, 'fullOnlinePaymentPrice'>} state - состояние с полем fullOnlinePaymentPrice
 * @param {number} orderTotal - итоговая сумма заказа
 * @return {number} сумма к оплате при выборе "Оплатить всю сумму"
 */
export function resolveFullPaymentAmount(
  state: Pick<BookingState, 'fullOnlinePaymentPrice'>,
  orderTotal: number
): number {
  const amount = Number(state.fullOnlinePaymentPrice ?? orderTotal)
  return Math.min(orderTotal, Math.max(0, amount))
}

export type BookingPaymentTiming = 'later' | 'prepay' | 'full'

export function resolveAvailablePaymentTimings(
  state: Pick<
    BookingState,
    'paymentMethods' | 'prepayMode' | 'fullOnlinePaymentDisabled'
  >,
  slotRequiresPrepay: boolean
): BookingPaymentTiming[] {
  if (!state.paymentMethods?.length) return ['later']

  const mustPrepay =
    slotRequiresPrepay ||
    state.prepayMode === 'required' ||
    state.prepayMode === 'partial_required' ||
    state.prepayMode === 'full_required'

  const timings = (
    ['later', 'prepay', 'full'] as BookingPaymentTiming[]
  ).filter((timing) => {
    if (timing === 'later') return !mustPrepay
    if (timing === 'prepay') return state.prepayMode !== 'none'
    // 'full': квест с флагом «Убрать "Оплатить всю сумму"» принимает онлайн
    // только частичную предоплату — вариант не предлагаем.
    return !state.fullOnlinePaymentDisabled
  })

  // Конфликт конфигурации: слот требует предоплату, а квест с prepay: 'none'
  // и включённым флагом не оставляет ни одного онлайн-варианта. Без фолбэка
  // клиент не увидел бы ни одной опции вовсе (сервер онлайн-платёж на таком
  // квесте всё равно отклонит) — оставляем оплату на месте.
  return timings.length > 0 ? timings : ['later']
}

export function getRequiredBookingServices(
  state: BookingState
): BookingState['services'] {
  return getBookingServicesCatalog(state).reduce<BookingState['services']>(
    (result, service) => {
      if (service.is_required) {
        result[Number(service.service_id)] = Math.max(
          1,
          Number(service.min) || 1
        )
      }

      return result
    },
    {}
  )
}

/**
 * Класс для управления состоянием бронирования
 */
export class BookingStateManager {
  private state: BookingState

  constructor(initialState?: Partial<BookingState>) {
    this.state = this.getDefaultState()
    if (initialState) {
      this.state = { ...this.state, ...initialState }
    }
  }

  /**
   * Получить дефолтное состояние
   * @return {BookingState} дефолтное состояние
   */
  private getDefaultState(): BookingState {
    const modal = document.querySelector('[data-booking-modal]')
    const nameInput = modal?.querySelector(
      'input[name="name"]'
    ) as HTMLInputElement
    const emailInput = modal?.querySelector(
      'input[name="email"]'
    ) as HTMLInputElement

    return {
      gameId: null,
      bookingPrice: null,
      servicesPrice: null,
      prepayMode: 'none',
      fullOnlinePaymentDisabled: false,
      prepayAmount: null,
      fullOnlinePaymentPrice: null,
      step: 1,
      closeStep: 1,
      isClosing: false,
      time: null,

      quest: null,
      slot: null,

      playersCount: 1,
      selectedTariff: null,
      tickets: null,
      language: 'ru',
      name: nameInput ? nameInput.value : '',
      phone: '',
      email: emailInput ? emailInput.value : '',
      promoCode: '',
      promoDiscount: null,
      certificateNumber: '',
      certificateCode: '',
      certificateDiscount: null,
      certificateSummary: null,
      paymentType: 'kassa',
      paymentTiming: 'later',
      paymentMethods: null,
      services: {},
      ticketTariffs: {},
      partial: false
    }
  }

  /**
   * Получить текущее состояние
   * @return {BookingState} текущее состояние
   */
  getState(): BookingState {
    return this.state
  }

  /**
   * Обновить состояние
   * @param {Partial<BookingState>} updates - частичное обновление состояния
   */
  setState(updates: Partial<BookingState>): void {
    this.state = { ...this.state, ...updates }
  }

  /**
   * Сбросить состояние к дефолтному
   */
  reset(): void {
    this.state = this.getDefaultState()
  }

  /**
   * Получить минимальное количество игроков
   * @return {number} минимальное количество игроков
   */
  getMinPlayersCount(): number {
    if (!this.state.slot) return 1
    const players = (this.state.slot.prices || [])
      .map((p) => Number(p.players))
      .filter((value) => Number.isFinite(value))

    // Без сетки цен по игрокам границы счётчика задаёт карточка квеста.
    if (players.length === 0) return this.state.quest?.players_min || 1

    return Math.min(...players)
  }

  /**
   * Получить максимальное количество игроков
   * @return {number} максимальное количество игроков
   */
  getMaxPlayersCount(): number {
    if (!this.state.slot) return 1
    const players = (this.state.slot.prices || [])
      .map((p) => Number(p.players))
      .filter((value) => Number.isFinite(value))

    if (players.length === 0) {
      return Math.max(
        this.state.quest?.players_max || 1,
        this.getMinPlayersCount()
      )
    }

    return Math.max(...players)
  }

  /**
   * Рассчитать итоговую стоимость
   * @return {number} итоговая стоимость
   */
  calculateTotal(): number {
    const gameTotal = this.calculateBookingBaseTotal()

    // Промокод (как и на сервере, Api::BookingController#apply_promo_code!)
    // уменьшает только цену игры, доп.услуги идут поверх скидки.
    const promoDiscount = this.state.promoDiscount
      ? (this.state.promoDiscount.amount ??
        this.calculatePromoDiscount(gameTotal))
      : 0
    let total =
      Math.max(0, gameTotal - promoDiscount) + this.calculateServicesTotal()

    // Применить скидку сертификата
    if (this.state.certificateDiscount) {
      total = Math.max(0, total - this.state.certificateDiscount)
    }

    return total
  }

  calculateBookingBaseTotal(): number {
    if (!this.state.slot) return 0

    if (this.state.quest?.ticket_system) {
      const tariffs = this.state.quest.ticket_system_tariffs || []
      return Object.entries(this.state.ticketTariffs).reduce(
        (sum, [index, quantity]) => {
          const tariff = tariffs[Number(index)]
          return tariff ? sum + tariff.price * quantity : sum
        },
        0
      )
    }

    const ticketPrice = Number(this.state.slot.tickets?.price)
    const ticketsCount = this.state.tickets?.count || 1
    if (Number.isFinite(ticketPrice)) return ticketPrice * ticketsCount

    // Режим тарифов: цена берётся из выбранного тарифа (только имя и цена, без игроков).
    if (this.state.selectedTariff) return this.state.selectedTariff.price

    const selectedPrice = (this.state.slot.prices || []).find(
      (p) => Number(p.players) === this.state.playersCount
    )?.price
    const minPrice = Number(this.state.slot.min_price)

    return selectedPrice || (Number.isFinite(minPrice) ? minPrice : 0)
  }

  calculateServicesTotal(): number {
    if (!this.state.slot) return 0

    const servicesCatalog = getBookingServicesCatalog(this.state)

    if (servicesCatalog.length === 0) return 0

    let servicesTotal = 0

    for (const [serviceId, count] of Object.entries(this.state.services)) {
      const service = servicesCatalog.find(
        (entry) => Number(entry.service_id) === Number(serviceId)
      )

      if (service?.price) {
        servicesTotal += service.price * count
      }
    }

    return servicesTotal
  }

  /**
   * Рассчитать размер скидки по промокоду
   * @param {number} baseTotal - базовая сумма без скидки
   * @return {number} размер скидки в рублях
   */
  calculatePromoDiscount(baseTotal: number): number {
    if (!this.state.promoDiscount || baseTotal <= 0) return 0

    // Скидка не больше самой цены игры (PromoCodeType#discount_amount).
    if (this.state.promoDiscount.type === 'money') {
      return Math.min(this.state.promoDiscount.value, baseTotal)
    } else if (this.state.promoDiscount.type === 'percent') {
      return Math.min(
        Math.round((baseTotal * this.state.promoDiscount.value) / 100),
        baseTotal
      )
    }

    return 0
  }

  /**
   * Количество, на которое делится сумма для цены «за игрока».
   * В режиме тарифов берётся число игроков из ответа сервера (selectedTariff.players),
   * т. к. счётчик игроков в этом режиме скрыт.
   * @return {number} делитель для расчёта цены за игрока
   */
  perPlayerDenominator(): number {
    if (this.state.slot?.tickets) return this.state.tickets?.count || 1

    const tariffPlayers = this.state.selectedTariff?.players
    if (tariffPlayers && tariffPlayers > 0) return tariffPlayers

    return this.state.playersCount
  }

  /**
   * Рассчитать стоимость на игрока
   * @return {number} стоимость на игрока
   */
  calculatePerPlayer(): number {
    const total = this.calculateBookingBaseTotal()

    return Math.floor(total / Math.max(1, this.perPlayerDenominator()))
  }
}
